import { Component, Input, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter, take, switchMap } from 'rxjs/operators';

import { UserService } from '_services/user.service';
import { WelcomeService } from '_services/welcome.service';
import { EclService, EclMockScenario, ECL_MOCK_SCENARIOS } from '_services/ecl.service';
import { TitleService } from '_services/title.service';
import { environment } from '../../../environments/environment';

/**
 * Longest a user should ever watch the spinner before we offer a way out.
 * This component is only alive while the resume route is showing, so "the
 * timer fired" and "the user is still stuck here" are the same thing.
 */
const STALL_TIMEOUT_MS = 30_000;

/**
 * Landing spinner for the College Board login return
 * (/app/:schoolId/resume/:pendingSessionId?resumeRoute=...). Dumb by design:
 * it feeds params into WelcomeService, bootstraps the school, and lets the
 * CLAIM branch of checkForSession promote the pending snapshot and navigate
 * onward. Params in, navigation out — no business logic lives here.
 *
 * The one thing it does own is not stranding anybody: every step between here
 * and that navigation is a network call that can hang rather than fail (auth,
 * preferences, questions, the claim callable). A watchdog turns any such hang
 * into a recovery screen instead of an endless spinner.
 */
@Component({
  selector: 'app-resume',
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatProgressSpinnerModule],
  template: `
    <main role="main" id="content" tabindex="-1">
      <div class="resume">
        @if (message(); as text) {
          <div class="resume__card resume__card--error" role="alert">
            <h1 class="resume__title">We couldn't sign you in</h1>
            <p class="resume__subtitle">{{ text }}</p>
            <div class="resume__actions">
              <button mat-raised-button color="primary" type="button" (click)="tryAgain()">Try again</button>
              <button mat-button type="button" (click)="startOver()">Continue without your saved answers</button>
            </div>
          </div>
        } @else {
          <div class="resume__card" role="status" aria-live="polite">
            <mat-progress-spinner
              class="resume__spinner"
              mode="indeterminate"
              color="primary"
              [diameter]="56"
              [strokeWidth]="4"
              aria-hidden="true">
            </mat-progress-spinner>
            <h1 class="resume__title">Signing you in</h1>
            <p class="resume__subtitle">Restoring your saved answers&hellip;</p>
          </div>
        }
      </div>
    </main>
  `,
  styles: [`
    .resume {
      display: flex;
      align-items: center;
      justify-content: center;
      min-height: 60vh;
      padding: 2rem 1rem;
    }
    .resume__card {
      display: flex;
      flex-direction: column;
      align-items: center;
      text-align: center;
      gap: 1rem;
      max-width: 26rem;
      width: 100%;
    }
    .resume__spinner {
      margin-bottom: 0.25rem;
    }
    .resume__title {
      margin: 0;
      font-size: 1.5rem;
      font-weight: 600;
      color: rgba(0, 0, 0, 0.87);
    }
    .resume__subtitle {
      margin: 0;
      font-size: 1rem;
      color: rgba(0, 0, 0, 0.6);
    }
    .resume__actions {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 0.5rem;
      margin-top: 0.5rem;
    }
    .resume__card--error .resume__title {
      color: #8c1621;
    }
  `],
})
export class ResumeComponent implements OnInit, OnDestroy {
  private titleService = inject(TitleService);
  we = inject(WelcomeService);
  userService = inject(UserService);
  private ecl = inject(EclService);
  private route = inject(ActivatedRoute);

  private school = '';
  private stallTimer: ReturnType<typeof setTimeout> | null = null;
  private errorSub: Subscription | null = null;

  /** Non-null once we have given up: whatever we show instead of the spinner. */
  readonly message = signal<string | null>(null);

  constructor() {
    this.titleService.setTitle('Signing in');
    this.we.route = this.route;
  }

  @Input()
  set schoolId(schoolId: string) {
    this.school = schoolId || '';
    this.userService.schoolId = this.school;
    this.loadSchoolAfterUser(this.school);
  }

  @Input()
  set pendingSessionId(pendingSessionId: string) {
    this.we.pendingSessionId = pendingSessionId || '';
    this.we.sessionState.next('loading');
  }

  @Input()
  set resumeRoute(resumeRoute: string) {
    this.we.resumeRoute = resumeRoute || '';
  }

  /** Mock-only: install the requested login scenario before the claim runs. */
  @Input()
  set fakeLogin(scenario: string) {
    if (!scenario || !environment.allowMockEclLogin) {
      return;
    }
    if (ECL_MOCK_SCENARIOS.some(s => s.id === scenario)) {
      this.ecl.installMockScenario(scenario as EclMockScenario);
    }
  }

  ngOnInit() {
    // Read in ngOnInit, not the setter: input order is not guaranteed, and this
    // decision needs both params.
    if (!this.we.pendingSessionId) {
      // Nothing to claim — the URL is malformed, and no downstream branch would
      // ever navigate us off this route. Leave immediately rather than spin.
      console.warn('Resume URL carried no pending session id; continuing to the calculator');
      this.startOver();
      return;
    }

    this.stallTimer = setTimeout(() => {
      this.message.set(
        'This is taking longer than it should. Your answers may still be saved — trying again usually works.'
      );
    }, STALL_TIMEOUT_MS);

    // errorMsg is a long-lived BehaviorSubject; clear anything a previous
    // screen left behind so we only ever react to this attempt's failure.
    this.we.errorMsg.next(null);

    // Surface a real failure the moment it happens rather than waiting out the
    // watchdog. Only the first one matters; the watchdog covers silence.
    this.errorSub = this.we.errorMsg.pipe(filter(Boolean), take(1)).subscribe(error => {
      this.clearStallTimer();
      this.message.set(error);
    });
  }

  ngOnDestroy() {
    this.clearStallTimer();
    // take(1) may never fire, and errorMsg outlives this component.
    this.errorSub?.unsubscribe();
  }

  /**
   * Re-request the same resume URL. A full load, not a router navigation: we
   * only get here because something never finished, and a reload is the one
   * recovery that does not depend on whatever is stuck. The pending session
   * lives for 7 days, so a retry can still find the user's answers.
   */
  tryAgain() {
    window.location.reload();
  }

  /** Give up on the saved answers and go start the calculator. */
  startOver() {
    // isAidQuiz is set by loadSchool, which may be the thing that never finished —
    // derive it from the school id directly so the fallback is always right.
    const isAidQuiz = this.school === 'aidquiz';
    const route = isAidQuiz ? 'household-information' : 'dependency';
    this.we.pendingSessionId = '';
    this.we.resumeRoute = '';
    window.location.assign(`/app/${this.school}/${route}`);
  }

  private clearStallTimer() {
    if (this.stallTimer !== null) {
      clearTimeout(this.stallTimer);
      this.stallTimer = null;
    }
  }

  private loadSchoolAfterUser(schoolId: string) {
    this.userService.user.pipe(
      filter(user => !!user),
      take(1),
      switchMap(_user => this.we.loadSchool(schoolId))
    ).subscribe();
  }
}
